import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { TableWithSearch } from "@/components";
import type { DataTableFeatures } from "@/components/data-table/features";
import { useBreadCrumbContext } from "@/contexts/breadcrumb";
import { useCurrentStore, useCustomers, usePaginatedSearch } from "../../hooks";

const Customers = () => {
	const { t } = useTranslation();
	useBreadCrumbContext([{ label: t("customers.pageTitle") }]);
	const currentStore = useCurrentStore();
	const { searchQuery, page, setPage, onSearchQueryChange } =
		usePaginatedSearch();

	const { data, isLoading } = useCustomers({
		page,
		"q[firstnameOrLastnameOrPhoneOrEmailOrNotesCont]": searchQuery,
		"q[storeIdEq]": currentStore?.id,
		"q[s]": ["firstname", "lastname"],
	});

	const columnHelper = createColumnHelper<DataTableFeatures, Customer>();

	const columns = useMemo(
		() =>
			[
				columnHelper.accessor((item) => `${item.firstname} ${item.lastname}`, {
					header: t("customers.table.name"),
					cell: (props) => (
						<Link
							className="underline"
							to={`/customers/${props.row.original.id}/edit`}
						>
							{props.getValue()}
						</Link>
					),
				}),
				columnHelper.accessor("phone", { header: t("customers.table.phone") }),
				columnHelper.accessor("email", { header: t("customers.table.email") }),
			] as ColumnDef<DataTableFeatures, Customer>[],
		[t, columnHelper],
	);

	return (
		<TableWithSearch
			searchPlaceholder={t("customers.searchPlaceholder")}
			onSearchQueryChange={onSearchQueryChange}
			searchQuery={searchQuery}
			isLoading={isLoading}
			createUrl="/customers/new"
			createLabel={t("customers.createButtonLabel")}
			columns={columns}
			data={data?.data}
			currentPage={page}
			totalPages={data?.headers["total-pages"]}
			onPageChange={setPage}
		/>
	);
};

export default Customers;
